/**
 * src/state/actions/usePeopleActions.js
 *
 * Employee-domain action callbacks extracted from useAppState.js as
 * part of the file-splitting refactor. Each useCallback body is moved
 * verbatim — same statements, same dependency arrays, same closure
 * semantics.
 *
 * `saveEmployee` reads `state.roleDefinitions` to log role-assignment
 * deltas; for that we accept the full `state` so the body and deps
 * array stay byte-identical to the original.
 *
 * EMPA-03 (ADR-0008): employee records live in dbo.tenant_employees now.
 * `saveEmployee` still writes through `updateState` — usePersistence's save
 * cycle diffs `employees` against the last server-confirmed shape and posts
 * the changed records to employees-write, so EVERY component that edits an
 * employee is covered without each having to remember to. `deleteEmployee`
 * cannot work that way: absence from an array is "that client never fetched
 * it" at least as often as it is a deletion (GH #3903, ADR-0002 §3.5), so a
 * delete is issued explicitly, by id, right here.
 *
 * Sub-hook: usePeopleActions({ updateState, tenantId, session, state,
 *                              flushPendingSave, adoptStateVersion,
 *                              beginExternalStateWrite, adoptServerEmployee })
 *
 * Returns:
 *   saveEmployee, saveEmployeesBulk, deleteEmployee, addComplianceFeedback,
 *   logSensitiveDocAction, recordAcknowledgement, recordCompetenceAssessment
 */

import { useCallback } from 'react';
import { logActivity } from '../../api/activityLogger.js';
import { emitEngineEvent } from '../../engine/engineEventBus.js';
import { generateId } from '../../utils/formatting.js';
import { useCommitNow } from './useCommitNow.js';
import {
  deleteEmployeeRecord,
  recordAcknowledgementOnBehalf,
  recordCompetenceAssessment as postCompetenceAssessment,
} from '../../api/employeeApi.js';
import { forgetEmployeeVersion } from '../employeeVersions.js';
import {
  captureRecord,
  restoreRecord,
  beginRecordWrite,
  markRecordCommitted,
} from './optimisticRollback.js';
import { queueNetworkWrite } from '../networkRetryQueue.js';
import { isNetworkFailure, dispatchWriteErrorBanner } from '../../api/writeErrors.js';
import { adoptTenantStateVersion } from '../../api/tenantStateVersion.js';

export function usePeopleActions({
  updateState,
  tenantId,
  session: _session,
  state,
  flushPendingSave,
  adoptStateVersion: _adoptStateVersion, // GH #7714: adoption goes through adoptTenantStateVersion
  beginExternalStateWrite,
  adoptServerEmployee,
  adoptServerTasks,
}) {
  const commitNow = useCommitNow(flushPendingSave);

  /**
   * @param {object} employee
   * @param {object} [session]
   * @param {{ commit?: boolean }} [opts] - `commit: true` marks this a terminal
   *   commit (the Add/Edit Employee modal's Save): the write is issued now
   *   instead of after the debounce. Callers that save on every keystroke —
   *   EmployeeDetailModal's JML checklist notes — must leave it off.
   * @returns {number} the edit generation, for useSaveStatus().track()
   */
  const saveEmployee = useCallback(
    (employee, session, opts = {}) => {
      // Every employee record must carry a stable id — portal login, evidence,
      // and role-assignment records are all resolved by e.id === employeeId
      // downstream. Backfill defensively so no id-less record can be created here.
      if (!employee.id) employee.id = generateId();
      let isEdit = false;
      let prevEmployee = null;
      const generation = updateState((s) => {
        if (!s.employees) s.employees = [];
        const idx = s.employees.findIndex((e) => e.id === employee.id);
        isEdit = idx >= 0;
        if (isEdit) {
          prevEmployee = s.employees[idx];
          s.employees[idx] = employee;
        } else {
          s.employees = [employee, ...s.employees];
        }
      });

      // ── Engine event ──────────────────────────────────────
      emitEngineEvent('employee.saved', { employee });

      if (session) {
        logActivity(tenantId, session, {
          module: 'Employees',
          actionType: isEdit ? 'edited' : 'created',
          recordId: employee.id,
          recordLabel: employee.name,
        });

        // ── Role-specific activity logging ──────────────────
        if (isEdit && prevEmployee) {
          // GH #6269 (EMP-B-002): an unassigned-but-acknowledged role is now
          // preserved with `unassignedAt` rather than removed, so "assigned"
          // for logging purposes means active, not merely present.
          const prevRoleIds = new Set(
            (prevEmployee.assignedRoles || [])
              .filter((a) => !a.unassignedAt)
              .map((a) => a.roleDefId)
          );
          const newRoleIds = new Set(
            (employee.assignedRoles || []).filter((a) => !a.unassignedAt).map((a) => a.roleDefId)
          );
          const roleDefs = state.roleDefinitions || [];

          // Log new role assignments
          for (const rid of newRoleIds) {
            if (!prevRoleIds.has(rid)) {
              const rd = roleDefs.find((r) => r.id === rid);
              logActivity(tenantId, session, {
                module: 'Employees',
                actionType: 'role_assigned',
                recordId: employee.id,
                recordLabel: `${employee.name} — Role: ${rd?.name || rid}`,
              });
            }
          }

          // Log role removals
          for (const rid of prevRoleIds) {
            if (!newRoleIds.has(rid)) {
              const rd = roleDefs.find((r) => r.id === rid);
              logActivity(tenantId, session, {
                module: 'Employees',
                actionType: 'role_removed',
                recordId: employee.id,
                recordLabel: `${employee.name} — Role removed: ${rd?.name || rid}`,
              });
            }
          }

          // Log acknowledgements
          for (const ar of employee.assignedRoles || []) {
            const prev = (prevEmployee.assignedRoles || []).find(
              (a) => a.roleDefId === ar.roleDefId
            );
            if (
              ar.acknowledgedAt &&
              (!prev || !prev.acknowledgedAt || prev.acknowledgedVersion !== ar.acknowledgedVersion)
            ) {
              const rd = roleDefs.find((r) => r.id === ar.roleDefId);
              logActivity(tenantId, session, {
                module: 'Employees',
                actionType: 'role_acknowledged',
                recordId: employee.id,
                recordLabel: `${employee.name} — Acknowledged: ${rd?.name || ar.roleDefId} v${ar.acknowledgedVersion}`,
              });
            }
          }
        }
      }

      // Terminal record commit: issue the write now rather than after the
      // debounce, and hand the edit generation back so the caller can confirm
      // the write landed instead of assuming it did (STG-B-003).
      if (opts.commit) commitNow();
      return generation;
    },
    [updateState, tenantId, state.roleDefinitions, commitNow]
  );

  /**
   * Excel import: apply a batch of create/update records in one write instead
   * of one `updateState` per row — an import of a few hundred rows should not
   * fire a few hundred debounced saves. Preserved sub-records (assets,
   * acknowledgements, JML history, etc.) are already folded into each
   * `employee` object by the caller (buildEmployeesFromRows spreads the
   * existing record before applying the import patch), so this is a plain
   * upsert-by-id.
   *
   * @param {Array<{action: 'create'|'update', employee: object, label: string}>} records
   * @param {object} [session]
   * @returns {number} the edit generation, for useSaveStatus().track()
   */
  const saveEmployeesBulk = useCallback(
    (records, session) => {
      const list = records || [];
      if (!list.length) return null;

      const withIds = list.map((r) => ({
        ...r,
        employee: r.employee.id ? r.employee : { ...r.employee, id: generateId() },
      }));

      const generation = updateState((s) => {
        if (!s.employees) s.employees = [];
        const updatedById = new Map(
          withIds.filter((r) => r.action === 'update').map((r) => [r.employee.id, r.employee])
        );
        const created = withIds.filter((r) => r.action === 'create').map((r) => r.employee);
        s.employees = [...created, ...s.employees.map((e) => updatedById.get(e.id) || e)];
      });

      emitEngineEvent('employee.saved', { bulk: true, count: withIds.length });

      if (session) {
        const createdCount = withIds.filter((r) => r.action === 'create').length;
        const updatedCount = withIds.length - createdCount;
        logActivity(tenantId, session, {
          module: 'Employees',
          actionType: 'imported',
          recordId: null,
          recordLabel: `Excel import — ${createdCount} created, ${updatedCount} updated`,
        });
      }

      commitNow();
      return generation;
    },
    [updateState, tenantId, commitNow]
  );

  const deleteEmployee = useCallback(
    (id, label, session) => {
      // Explicit per-record delete against employees-write (ADR-0008 §2.4).
      // The optimistic removal below is what the user sees; it is rolled back
      // if the server refuses, so the list never shows an employee as gone
      // that the server still holds — and never the reverse.
      let before = null;
      const fenceKey = `employee:${id}`;
      const fence = beginRecordWrite(fenceKey);
      const generation = updateState((s) => {
        before = captureRecord(s.employees, id);
        s.employees = (s.employees || []).filter((e) => e.id !== id);
      });

      const release = beginExternalStateWrite ? beginExternalStateWrite() : () => {};
      const attempt = () => deleteEmployeeRecord(id, tenantId);
      Promise.resolve()
        .then(attempt)
        .then((result) => {
          markRecordCommitted(fenceKey);
          forgetEmployeeVersion(id);
          // employees-write commits the blob's employees key too, so it moves
          // tenant_state's row version — hand it back or the next debounced
          // save sends a stale one and self-conflicts.
          // GH #7714: the checked path, never the raw hook.
          adoptTenantStateVersion(result);
          // RCQA-04: the gap tasks the delete closed, adopted whatever the version did.
          adoptServerTasks?.(result?.tasks);
        })
        .catch((e) => {
          // #4991's contract: the list never shows a record the server has not
          // stored, and that holds in both directions — a refused delete puts
          // the employee back.
          updateState((s) => {
            s.employees = restoreRecord(s.employees, id, before, { fence });
          });
          if (isNetworkFailure(e)) {
            // A write that never reached the server is retried when
            // connectivity returns.
            queueNetworkWrite({
              key: fenceKey,
              error: e,
              attempt,
              onSuccess: (result) => {
                forgetEmployeeVersion(id);
                updateState((s) => {
                  s.employees = (s.employees || []).filter((emp) => emp.id !== id);
                });
                adoptTenantStateVersion(result);
                adoptServerTasks?.(result?.tasks);
              },
            });
          } else {
            // GH #6271: a refusal is final — the restore above is the answer,
            // and the row silently reappearing with no explanation is exactly
            // the gap this closes. employees-write's 403/422 refusals (EMP-B-002
            // evidence, an unreadable record) carry a message written to be
            // shown directly.
            dispatchWriteErrorBanner(e?.message);
          }
        })
        .finally(() => release());

      if (session) {
        logActivity(tenantId, session, {
          module: 'Employees',
          actionType: 'deleted',
          recordId: id,
          recordLabel: label || id,
        });
      }

      // A delete is always terminal — the modal closes on it and the row is
      // gone from the list, so there is nothing left for the debounce to
      // coalesce and a navigation inside that window would strand the delete.
      commitNow();
      return generation;
    },
    [updateState, tenantId, commitNow, beginExternalStateWrite, adoptServerTasks]
  );

  const addComplianceFeedback = useCallback(
    (employeeId, feedback, session) => {
      let employeeName = '';
      updateState((s) => {
        if (!s.employees) return;
        const emp = s.employees.find((e) => e.id === employeeId);
        if (!emp) return;
        employeeName = emp.name;
        if (!emp.feedbacks) emp.feedbacks = [];
        emp.feedbacks.push(feedback);
      });

      if (session && employeeName) {
        logActivity(tenantId, session, {
          module: 'Employees',
          actionType: `feedback_${feedback.type}`,
          recordId: employeeId,
          recordLabel: employeeName,
        });
      }
    },
    [updateState, tenantId]
  );

  // Log sensitive document status changes
  const logSensitiveDocAction = useCallback(
    (employee, docLabel, action, session) => {
      if (session) {
        logActivity(tenantId, session, {
          module: 'Employees',
          actionType: `sensitive_doc_${action}`,
          recordId: employee.id,
          recordLabel: `${employee.name} — ${docLabel}`,
        });
      }
    },
    [tenantId]
  );

  /**
   * Record a read on an employee's behalf (RDRC-04A). Goes through
   * employees-write's record-acknowledgement action — never updateState: a
   * whole-record save that adds an acknowledgement is refused, because only
   * the server may stamp who recorded it, for whom, why and when. The record
   * the server committed is adopted as-is, so the save cycle does not post it
   * back. Rejects with the server's error (its `responseBody.code` names it).
   *
   * @param {{ employeeId: string, documentId: string, reason: string, evidenceRef?: string }} body
   * @returns {Promise<{ acknowledgement: object, alreadyRecorded?: boolean }>}
   */
  const recordAcknowledgement = useCallback(
    async (body) => {
      const result = await recordAcknowledgementOnBehalf(body, tenantId);
      if (result?.employee) adoptServerEmployee?.(result.employee);
      return result;
    },
    [tenantId, adoptServerEmployee]
  );

  /**
   * Record a competence assessment (RCQA-03) through employees-write's
   * record-competence-assessment action — never updateState, for the same
   * reason as recordAcknowledgement. The server's committed record is adopted,
   * so a concurrent assessor's append arrives with it. Rejects with the
   * server's error (`responseBody.code` names it).
   */
  const recordCompetenceAssessment = useCallback(
    async (body) => {
      const result = await postCompetenceAssessment(body, tenantId);
      if (result?.employee) adoptServerEmployee?.(result.employee);
      // RCQA-04: the gap task this assessment closed or reopened.
      adoptServerTasks?.(result?.tasks);
      return result;
    },
    [tenantId, adoptServerEmployee, adoptServerTasks]
  );

  return {
    saveEmployee,
    saveEmployeesBulk,
    deleteEmployee,
    addComplianceFeedback,
    logSensitiveDocAction,
    recordAcknowledgement,
    recordCompetenceAssessment,
  };
}
